import MoreVertIcon from '@mui/icons-material/MoreVert'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Menu from '@mui/material/Menu'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { DataGridPremium, type GridPaginationModel } from '@mui/x-data-grid-premium'
import type { PaymentOrderV2 } from '@mynt/backoffice-api-spec/payment'
import dayjs from 'dayjs'
import type React from 'react'
import { useRef, useState } from 'react'
import Spacings from '../../figma/tokens/Spacings'
import FigmaBox from '../../mynt-components/components/FigmaBox'
import { YYYY_MM_DD } from '../../tiger/Constants'
import { formatAmount } from '../../tiger/libs/Common'
import OutboundPaymentDetails from './OutboundPaymentDetails'

type OutboundPaymentsTableProps = {
  paymentOrders: PaymentOrderV2[]
  isLoading?: boolean
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  rowCount: number
  actions: (paymentOrder: PaymentOrderV2, onClose: () => unknown) => React.ReactNode
}

export const OutboundPaymentsTable = ({
  paymentOrders,
  isLoading,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  actions,
}: OutboundPaymentsTableProps) => {
  const showMinHeight = paymentOrders.length === 0 || isLoading

  return (
    <DataGridPremium
      sx={{ height: showMinHeight ? 300 : 'auto' }}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      rowCount={rowCount}
      initialState={{
        sorting: { sortModel: [{ field: 'scheduledPaymentDate', sort: 'desc' }] },
      }}
      pageSizeOptions={[10, 25, 50, 100]}
      getRowHeight={() => 'auto'}
      getDetailPanelContent={({ row }) => (
        <FigmaBox fullWidth fullPadding spacing={Spacings.large}>
          <OutboundPaymentDetails paymentOrder={row} />
        </FigmaBox>
      )}
      getDetailPanelHeight={() => 'auto'}
      loading={isLoading}
      slots={{
        loadingOverlay: () => <LinearProgress />,
        noRowsOverlay: () => (
          <Stack
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            No outbound payments found
          </Stack>
        ),
      }}
      rows={paymentOrders}
      columnHeaderHeight={64}
      columns={[
        {
          field: 'scheduledPaymentDate',
          headerName: 'Payment Date',
          type: 'date',
          minWidth: 130,
          valueFormatter: (value) => dayjs(value).format(YYYY_MM_DD),
          valueGetter: (value) => dayjs(value).toDate(),
        },
        {
          field: 'stateLabel',
          headerName: 'State',
          minWidth: 140,
        },
        {
          field: 'recipientName',
          headerName: 'Recipient',
          minWidth: 200,
          flex: 1,
          valueGetter: (_value, row) => row.destinationDetails?.recipient?.recipientName ?? '',
        },
        {
          field: 'amount.value',
          headerName: 'Amount',
          minWidth: 150,
          valueGetter: (_value, row) => row.destinationDetails?.amount?.value,
          renderCell: ({ row }) => {
            const amount = row.destinationDetails?.amount
            if (!amount) return ''
            return formatAmount(amount)
          },
        },
        {
          field: 'senderIban',
          headerName: 'IBAN',
          minWidth: 220,
          valueGetter: (_value, row) => row.destinationDetails?.senderIban ?? '',
        },
        {
          field: 'source.ref',
          headerName: 'Source',
          minWidth: 150,
          valueGetter: (_value, row) => row.source?.ref ?? '',
        },
        {
          field: 'createdAt',
          headerName: 'Created',
          type: 'date',
          minWidth: 130,
          valueFormatter: (value) => dayjs(value).format(YYYY_MM_DD),
          valueGetter: (value) => dayjs(value).toDate(),
        },
        {
          field: 'actions',
          headerName: 'Actions',
          filterable: false,
          sortable: false,
          renderCell: ({ row }) => <ActionsColumn actions={actions} paymentOrder={row} />,
        },
      ]}
    />
  )
}

type ActionsColumnProps = {
  paymentOrder: PaymentOrderV2
  actions: OutboundPaymentsTableProps['actions']
}

const ActionsColumn = ({ paymentOrder, actions }: ActionsColumnProps) => {
  const ref = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const onClose = () => setOpen(false)

  return (
    <>
      <IconButton data-testid="more-actions" onClick={() => setOpen(true)} ref={ref}>
        <MoreVertIcon />
      </IconButton>
      <Menu open={open} anchorEl={ref.current} onClose={onClose}>
        <Paper sx={{ width: 325, maxWidth: '100%' }}>
          <MenuList>{actions(paymentOrder, onClose)}</MenuList>
        </Paper>
      </Menu>
    </>
  )
}
